/**
 * 🌐 MGI API Client Configuration
 * Cliente HTTP enterprise con Axios
 * Manejo de interceptors, errores y autenticación
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { getApiBaseUrl, isFrontendDevelopmentMode } from '@/config/runtimeConfig'

// 📍 Configuración base de API
// P12-TEST1A-FE3: do NOT resolve the API base URL at module evaluation. It is resolved lazily
// in the request interceptor (config.baseURL = getApiBaseUrl()), so importing this module never
// invokes a runtime resolver (closes the release TDZ class). Fail-closed still applies at use.
const API_CONFIG = {
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
}

// 🔧 Cliente Axios principal
const apiClient: AxiosInstance = axios.create(API_CONFIG)

// MGI-PILOT-SELF-SERVICE-CREDENTIAL-ROTATION-WS1: the development request log never prints a credential (the
// self-service rotation sends two passwords; login sends one). Only the LOG copy is redacted; the request is not.
export function redactCredentialFields(data: unknown): unknown {
  if (!data || typeof data !== 'object' || data instanceof FormData || Array.isArray(data)) return data
  return Object.fromEntries(Object.entries(data as Record<string, unknown>).map(([k, v]) => [k, /password|secret|token/i.test(k) ? '[redacted]' : v]))
}

// 📊 Interceptor de Requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 🌐 P12-TEST1A-FE3: resolve the runtime base URL at request time (lazy, fail-closed),
    // never at module evaluation. Preserve any explicit per-request config.baseURL.
    if (!config.baseURL) {
      config.baseURL = getApiBaseUrl()
    }

    // 🔐 Añadir token de autenticación si existe
    const token = localStorage.getItem('mgi_auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

      // 🏷️ Injectar Department Context para todas las rutas si está disponible (incluye inventario)
      try {
        const depId = (typeof localStorage !== 'undefined' && (localStorage.getItem('mgi_inventory_department_id') || localStorage.getItem('mgi_department_id'))) || (import.meta as any)?.env?.VITE_INVENTORY_DEPARTMENT_ID
        if (depId && config.headers && !(config.headers as any)['X-Department-Id']) {
          (config.headers as any)['X-Department-Id'] = depId
        }
      } catch (_) {
        // ignore
      }

    // � PR-22: FormData — eliminar Content-Type para que Axios setee boundary automáticamente
    if (config.data instanceof FormData && config.headers) {
      delete (config.headers as any)['Content-Type']
    }

    // �📝 Log de requests en desarrollo
    if (isFrontendDevelopmentMode) {
      console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        data: redactCredentialFields(config.data),
        params: config.params
      })
    }

    return config
  },
  (error: AxiosError) => {
    console.error('❌ Request Error:', error)
    return Promise.reject(error)
  }
)

// 📥 Interceptor de Responses (handler interno; el test valida el interceptor registrado en la instancia canónica).
/**
 * Desencapsula respuestas con formato { success, data } extrayendo `data` (comportamiento por DEFECTO,
 * sin cambios para las peticiones existentes). Opt-out por petición: si el config de la petición lleva
 * `skipUnwrap === true`, se PRESERVA el envelope completo en response.data (sobreviven
 * count/page/limit/totalPages). `success:false` SIEMPRE se convierte en error, con o sin el flag.
 * Aditivo y retrocompatible: token/baseURL/redirects/retries/normalización de errores no cambian.
 */
function unwrapApiResponse(response: AxiosResponse): AxiosResponse {
  // 📝 Log de responses en desarrollo
  if (isFrontendDevelopmentMode) {
    console.log(`✅ API Response: ${response.status}`, {
      url: response.config.url,
      data: response.data
    })
  }

  const cfg: any = response.config || {}
  const skipUnwrap = cfg.skipUnwrap === true

  // 🎁 Desencapsular respuestas con formato { success, data }
  const payload = response?.data
  if (payload && typeof payload === 'object' && 'success' in payload) {
    if (payload.success) {
      // ✅ Por defecto extraer `data`. Con skipUnwrap se conserva el envelope completo intacto.
      // Si no hay campo 'data', preservar el payload completo (ej. login: { success, token, user }).
      if (!skipUnwrap && 'data' in payload) {
        response.data = payload.data ?? null
      }
      console.log('🎁 Respuesta desencapsulada:', Array.isArray(response.data) ? `${response.data.length} items` : typeof response.data)
    } else {
      // ❌ Backend devolvió success: false, convertir a error (sin cambios; aplica también con skipUnwrap)
      const errorMsg = payload.error || payload.message || 'Operación fallida'
      const err: any = new Error(errorMsg)
      err.status = response.status
      err.response = response
      throw err
    }
  }

  return response
}

apiClient.interceptors.response.use(
  unwrapApiResponse,
  (error: AxiosError) => {
    // 🔄 Manejo centralizado de errores de autenticación / autorización
    const cfg: any = error.config || {}
    const skipHeader = cfg?.headers?.['x-skip-auth-redirect'] === 'true'
    const skipFlag = cfg?.skipAuthRedirect === true
    const skipAuthRedirect = skipHeader || skipFlag
    const status = error.response?.status
    const requestUrl = String(cfg?.url || '')

    // 🔐 401 — sesión expirada/ inválida: limpiar sesión y enviar a login.
    // G04-FE safeguard (H-C2): NO redirigir cuando (a) skip explícito,
    // (b) es un endpoint de autenticación (login/test-login/impersonate), donde el
    // caller muestra el error inline, o (c) ya estamos en /login (evita login-loop).
    if (status === 401) {
      const isAuthEndpoint = /\/auth\/(login|test-login|impersonate)\b/.test(requestUrl)
      const alreadyOnLogin =
        typeof window !== 'undefined' && !!window.location?.pathname?.startsWith('/login')
      if (!skipAuthRedirect && !isAuthEndpoint && !alreadyOnLogin) {
        console.warn('🔐 Token expirado/ inválido, redirigiendo a login...')
        localStorage.removeItem('mgi_auth_token')
        localStorage.removeItem('mgi_user_data')
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }

    // ⛔ 403 — autenticado pero SIN autorización (rol/alcance fuera de alcance).
    // G04-FE (H-C): ruta dedicada distinta del 401.
    //   - NO se limpia el token: la sesión sigue siendo válida.
    //   - Se marca el error como access-denied para que el caller lo distinga.
    //   - Por defecto NO se fuerza navegación (un 403 de una llamada de fondo no debe
    //     sacar al usuario de su vista). Redirección a /access-denied es opt-in vía
    //     config.redirectOnForbidden o header 'x-redirect-on-forbidden'.
    if (status === 403) {
      const accessErr: any = error
      accessErr.isAccessDenied = true
      console.warn('⛔ Acceso denegado (403) — autenticado sin permiso/ alcance:', requestUrl)
      const redirectOnForbidden =
        cfg?.redirectOnForbidden === true ||
        cfg?.headers?.['x-redirect-on-forbidden'] === 'true'
      if (
        redirectOnForbidden &&
        typeof window !== 'undefined' &&
        !window.location?.pathname?.startsWith('/access-denied')
      ) {
        window.location.href = '/access-denied'
      }
      return Promise.reject(error)
    }

    // 📝 Log de errores
    console.error('❌ API Error:', {
      status: error.response?.status,
      message: error.message,
      url: error.config?.url,
      data: error.response?.data
    })

    return Promise.reject(error)
  }
)

// 🎯 Tipos para responses de API
export interface ApiResponse<T = any> {
  data: T
  message?: string
  status: 'success' | 'error'
  timestamp?: string
}

export interface PaginatedResponse<T = any> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

// 🛠️ Utilities para requests
export class ApiClient {
  
  // 📤 GET Request
  static async get<T = any>(
    endpoint: string, 
    params?: Record<string, any>
  ): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.get(endpoint, { params })
      return response.data
    } catch (error) {
      throw this.handleError(error as AxiosError)
    }
  }

  // 📤 PATCH Request
  static async patch<T = any>(
    endpoint: string,
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.patch(endpoint, data)
      return response.data
    } catch (error) {
      throw this.handleError(error as AxiosError)
    }
  }

  // 📤 POST Request
  static async post<T = any>(
    endpoint: string, 
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.post(endpoint, data)
      return {
        data: response.data,
        status: 'success' as const,
        timestamp: new Date().toISOString()
      }
    } catch (error) {
      throw this.handleError(error as AxiosError)
    }
  }

  // 📤 PUT Request
  static async put<T = any>(
    endpoint: string, 
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.put(endpoint, data)
      return response.data
    } catch (error) {
      throw this.handleError(error as AxiosError)
    }
  }

  // 📤 DELETE Request
  static async delete<T = any>(
    endpoint: string
  ): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.delete(endpoint)
      return response.data
    } catch (error) {
      throw this.handleError(error as AxiosError)
    }
  }

  // 🔍 Error Handler
  private static handleError(error: AxiosError): Error {
    const errorData = error.response?.data as any
    const message = errorData?.message || error.message || 'Error desconocido'
    const status = error.response?.status || 500
    
    return new Error(`[${status}] ${message}`)
  }
}

// 🌐 Export del cliente configurado
export default apiClient
export { apiClient }
